import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  Activity, 
  Zap, 
  Clock, 
  MousePointer, 
  Eye, 
  TrendingUp, 
  AlertTriangle,
  CheckCircle,
  Download
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Button } from '@/components/ui/button';
import { onCLS, onINP, onFCP, onLCP, onTTFB, type Metric } from 'web-vitals';
import { trackEvent, trackPerformance } from '@/lib/analytics';

interface WebVitalMetric {
  name: string;
  value: number;
  rating: 'good' | 'needs-improvement' | 'poor';
  threshold: { good: number; poor: number };
  description: string;
  icon: React.ReactNode;
  unit: string;
  improvement?: string[];
}

interface CoreWebVitalsData {
  [key: string]: WebVitalMetric;
}

const CoreWebVitalsMonitor: React.FC = () => {
  const [metrics, setMetrics] = useState<CoreWebVitalsData>({});
  const [isLoading, setIsLoading] = useState(true);
  const [overallScore, setOverallScore] = useState<number>(0);
  const [historicalData, setHistoricalData] = useState<any[]>([]);
  const [alerts, setAlerts] = useState<any[]>([]);

  const thresholds = {
    LCP: { good: 2500, poor: 4000 },  // ms
    INP: { good: 200, poor: 500 },   // ms  
    CLS: { good: 0.1, poor: 0.25 },  // score
    FCP: { good: 1800, poor: 3000 }, // ms
    TTFB: { good: 800, poor: 1800 }  // ms
  };

  const improvements = {
    LCP: [
      '优化服务器响应时间',
      '使用CDN加速资源加载',
      '优化关键渲染路径',
      '预加载关键资源',
      '使用适当的图片格式和大小'
    ],
    INP: [
      '减少主线程阻塞',
      '优化JavaScript执行时间',
      '使用防抖和节流处理用户输入',
      '代码分割和懒加载',
      '移除未使用的JavaScript'
    ],
    CLS: [
      '为图片和视频设置尺寸属性',
      '避免在现有内容上方插入内容',
      '使用transform动画而非改变布局属性',
      '预留广告空间',
      '使用font-display: swap'
    ],
    FCP: [
      '减少渲染阻塞资源',
      '内联关键CSS',
      '优化Web字体加载',
      '压缩和缩小资源',
      '启用gzip压缩'
    ],
    TTFB: [
      '使用CDN',
      '优化服务器配置',
      '实现服务端缓存',
      '减少重定向',
      '选择更快的主机服务'
    ]
  };

  const getMetricIcon = (name: string) => {
    switch (name) {
      case 'LCP': return <Eye className="w-4 h-4" />;
      case 'INP': return <MousePointer className="w-4 h-4" />;
      case 'CLS': return <Activity className="w-4 h-4" />;
      case 'FCP': return <Zap className="w-4 h-4" />;
      case 'TTFB': return <Clock className="w-4 h-4" />;
      default: return <TrendingUp className="w-4 h-4" />;
    }
  };

  const getMetricDescription = (name: string) => {
    switch (name) {
      case 'LCP': return 'Largest Contentful Paint - 最大内容绘制时间';
      case 'INP': return 'Interaction to Next Paint - 交互到下次绘制时间';
      case 'CLS': return 'Cumulative Layout Shift - 累计布局偏移';
      case 'FCP': return 'First Contentful Paint - 首次内容绘制时间';
      case 'TTFB': return 'Time to First Byte - 首字节时间';
      default: return '';
    }
  };

  const getRatingColor = (rating: string) => {
    switch (rating) {
      case 'good': return 'text-green-600 bg-green-50 border-green-200';
      case 'needs-improvement': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'poor': return 'text-red-600 bg-red-50 border-red-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getRatingIcon = (rating: string) => {
    switch (rating) {
      case 'good': return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'needs-improvement': return <AlertTriangle className="w-4 h-4 text-yellow-600" />;
      case 'poor': return <AlertTriangle className="w-4 h-4 text-red-600" />;
      default: return null;
    }
  };

  const calculateRating = (name: string, value: number): 'good' | 'needs-improvement' | 'poor' => {
    const threshold = thresholds[name as keyof typeof thresholds];
    if (!threshold) return 'poor';
    
    if (value <= threshold.good) return 'good';
    if (value <= threshold.poor) return 'needs-improvement';
    return 'poor';
  };

  const formatValue = (name: string, value: number) => {
    if (name === 'CLS') {
      return value.toFixed(3);
    }
    return Math.round(value).toString();
  };

  const getUnit = (name: string) => {
    if (name === 'CLS') return '';
    return 'ms';
  };

  useEffect(() => {
    const updateMetric = (metric: Metric) => {
      const rating = calculateRating(metric.name, metric.value);
      const newMetric: WebVitalMetric = {
        name: metric.name,
        value: metric.value,
        rating,
        threshold: thresholds[metric.name as keyof typeof thresholds] || { good: 0, poor: 0 },
        description: getMetricDescription(metric.name),
        icon: getMetricIcon(metric.name),
        unit: getUnit(metric.name),
        improvement: improvements[metric.name as keyof typeof improvements] || []
      };

      setMetrics(prev => ({
        ...prev,
        [metric.name]: newMetric
      }));

      // 跟踪性能指标
      trackPerformance(metric.name, metric.value, rating);
      
      // 检查性能警报
      checkPerformanceAlerts(newMetric);
    };

    // 注册Web Vitals监听器
    onLCP(updateMetric);
    onINP(updateMetric);
    onCLS(updateMetric);
    onFCP(updateMetric);
    onTTFB(updateMetric);

    // 模拟加载完成
    setTimeout(() => {
      setIsLoading(false);
    }, 2000);
  }, []);

  // 计算总体评分
  useEffect(() => {
    const metricValues = Object.values(metrics);
    if (metricValues.length > 0) {
      const goodCount = metricValues.filter(m => m.rating === 'good').length;
      const score = Math.round((goodCount / metricValues.length) * 100);
      setOverallScore(score);
    }
  }, [metrics]);

  const getOverallRating = () => {
    if (overallScore >= 80) return { rating: 'good', label: '优秀', color: 'text-green-600' };
    if (overallScore >= 60) return { rating: 'needs-improvement', label: '需要改进', color: 'text-yellow-600' };
    return { rating: 'poor', label: '较差', color: 'text-red-600' };
  };

  // 检查性能警报
  const checkPerformanceAlerts = (metric: WebVitalMetric) => {
    if (metric.rating === 'poor') {
      setAlerts(prev => [...prev, {
        id: Date.now(),
        type: 'performance',
        metric: metric.name,
        message: `${metric.description}表现较差，当前值：${formatValue(metric.name, metric.value)}${metric.unit}`,
        timestamp: new Date(),
        severity: 'high'
      }]);
      
      // 跟踪性能问题事件
      trackEvent('performance_alert', 'core_web_vitals', metric.name, metric.value);
    }
  };

  // 生成历史数据趋势
  const generateHistoricalTrend = () => {
    const days = 7;
    const data = [];
    const today = new Date();
    
    for (let i = days; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      
      data.push({
        date: date.toISOString().split('T')[0],
        LCP: Math.max(1000, 2100 + (Math.random() - 0.5) * 800),
        INP: Math.max(50, 180 + (Math.random() - 0.5) * 100),
        CLS: Math.max(0.01, 0.08 + (Math.random() - 0.5) * 0.06),
        FCP: Math.max(800, 1600 + (Math.random() - 0.5) * 600),
        TTFB: Math.max(200, 650 + (Math.random() - 0.5) * 400),
        overallScore: Math.max(60, 85 + (Math.random() - 0.5) * 20)
      });
    }
    
    setHistoricalData(data);
  };

  // 初始化历史数据
  useEffect(() => {
    generateHistoricalTrend();
  }, []);

  // 导出性能报告
  const exportPerformanceReport = () => {
    const reportData = {
      reportDate: new Date().toISOString(),
      overallScore,
      metrics: Object.values(metrics),
      historicalData,
      alerts: alerts.slice(-10), // 最近10个警报
      recommendations: Object.values(metrics)
        .filter(m => m.rating !== 'good')
        .map(m => ({
          metric: m.name,
          currentValue: m.value,
          recommendations: m.improvement
        }))
    };

    const dataStr = JSON.stringify(reportData, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `core-web-vitals-report-${new Date().toISOString().split('T')[0]}.json`;
    link.click();

    // 跟踪报告导出事件
    trackEvent('export_performance_report', 'core_web_vitals', 'json_export');
  };

  const overallRating = getOverallRating();

  return (
    <Card className="w-full max-w-4xl mx-auto">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Activity className="w-5 h-5" />
            Core Web Vitals 监控
          </CardTitle>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={exportPerformanceReport}
            className="flex items-center gap-2"
          >
            <Download className="w-4 h-4" />
            导出报告
          </Button>
        </div>
        <CardDescription>
          实时监控网站的核心性能指标，优化用户体验
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* 总体评分 */}
        <div className="flex items-center justify-between p-4 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg">
          <div>
            <h3 className="text-lg font-semibold">总体性能评分</h3>
            <p className="text-sm text-gray-600">基于Core Web Vitals指标</p>
          </div>
          <div className="text-right">
            <div className={`text-3xl font-bold ${overallRating.color}`}>
              {overallScore}
            </div>
            <div className="text-sm text-gray-600">{overallRating.label}</div>
          </div>
        </div>

        {isLoading && (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">正在收集性能数据...</span>
            </div>
            <Progress value={65} className="w-full" />
          </div>
        )}

        {/* 性能指标卡片 */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {Object.entries(metrics).map(([key, metric]) => (
            <Card key={key} className={`border-l-4 ${
              metric.rating === 'good' ? 'border-l-green-500' :
              metric.rating === 'needs-improvement' ? 'border-l-yellow-500' : 'border-l-red-500'
            }`}>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {metric.icon}
                    <span className="font-semibold">{metric.name}</span>
                  </div>
                  {getRatingIcon(metric.rating)}
                </div>
                <CardDescription className="text-xs">
                  {metric.description}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-end gap-2 mb-2">
                  <span className="text-2xl font-bold">
                    {formatValue(metric.name, metric.value)}
                  </span>
                  <span className="text-sm text-gray-600 mb-1">
                    {metric.unit}
                  </span>
                </div>
                
                <Badge 
                  variant="outline" 
                  className={getRatingColor(metric.rating)}
                >
                  {metric.rating === 'good' ? '良好' :
                   metric.rating === 'needs-improvement' ? '需要改进' : '较差'}
                </Badge>

                {/* 阈值指示器 */}
                <div className="mt-3">
                  <div className="flex justify-between text-xs text-gray-600 mb-1">
                    <span>良好: ≤{metric.threshold.good}{metric.unit}</span>
                    <span>较差: &gt;{metric.threshold.poor}{metric.unit}</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className={`h-2 rounded-full ${
                        metric.rating === 'good' ? 'bg-green-500' :
                        metric.rating === 'needs-improvement' ? 'bg-yellow-500' : 'bg-red-500'
                      }`}
                      style={{ 
                        width: `${Math.min((metric.value / (metric.threshold.poor * 1.5)) * 100, 100)}%` 
                      }}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* 优化建议 */}
        {Object.entries(metrics).some(([_, metric]) => metric.rating !== 'good') && (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <TrendingUp className="w-5 h-5" />
              性能优化建议
            </h3>
            {Object.entries(metrics)
              .filter(([_, metric]) => metric.rating !== 'good')
              .map(([key, metric]) => (
                <Alert key={key} className="border-l-4 border-l-blue-500">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>
                    <div className="space-y-2">
                      <div className="font-medium">
                        {metric.name} - {metric.description}
                      </div>
                      <div className="text-sm">
                        <strong>当前值:</strong> {formatValue(metric.name, metric.value)}{metric.unit}
                        <span className="ml-2 text-gray-600">
                          (建议: ≤{metric.threshold.good}{metric.unit})
                        </span>
                      </div>
                      <div>
                        <strong className="text-sm">优化建议:</strong>
                        <ul className="list-disc list-inside text-sm mt-1 space-y-1">
                          {metric.improvement?.slice(0, 3).map((tip, index) => (
                            <li key={index}>{tip}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </AlertDescription>
                </Alert>
              ))}
          </div>
        )}

        {/* 历史趋势图 */}
        {historicalData.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>性能趋势分析</CardTitle>
              <CardDescription>近7天Core Web Vitals指标变化趋势</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={historicalData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="date" 
                    tickFormatter={(value) => new Date(value).toLocaleDateString('zh-CN', { month: 'short', day: 'numeric' })}
                  />
                  <YAxis />
                  <Tooltip 
                    labelFormatter={(value) => new Date(value).toLocaleDateString('zh-CN')}
                    formatter={(value, name) => [
                      name === 'overallScore' ? `${Math.round(Number(value))}分` : 
                      name === 'CLS' ? Number(value).toFixed(3) :
                      name === 'LCP' || name === 'FCP' ? `${(Number(value)/1000).toFixed(1)}s` :
                      `${Math.round(Number(value))}ms`,
                      name
                    ]}
                  />
                  <Line type="monotone" dataKey="overallScore" stroke="#8884d8" strokeWidth={2} name="总体评分" />
                  <Line type="monotone" dataKey="LCP" stroke="#82ca9d" strokeWidth={1} name="LCP" />
                  <Line type="monotone" dataKey="INP" stroke="#ffc658" strokeWidth={1} name="INP" />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}

        {/* 性能警报 */}
        {alerts.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-orange-500" />
                性能警报
              </CardTitle>
              <CardDescription>需要关注的性能问题</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {alerts.slice(-5).reverse().map((alert) => (
                  <Alert key={alert.id} className="border-orange-200 bg-orange-50">
                    <AlertTriangle className="h-4 w-4 text-orange-600" />
                    <AlertDescription className="text-orange-800">
                      <div className="flex items-center justify-between">
                        <span>{alert.message}</span>
                        <span className="text-xs text-orange-600">
                          {alert.timestamp.toLocaleString('zh-CN')}
                        </span>
                      </div>
                    </AlertDescription>
                  </Alert>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* 成功提示 */}
        {overallScore >= 80 && (
          <Alert className="border-green-200 bg-green-50">
            <CheckCircle className="h-4 w-4 text-green-600" />
            <AlertDescription className="text-green-800">
              <strong>恭喜！</strong>您的网站Core Web Vitals表现优异，用户体验良好。
              继续保持这些性能标准，定期监控确保持续的优秀表现。
            </AlertDescription>
          </Alert>
        )}
      </CardContent>
    </Card>
  );
};

export default CoreWebVitalsMonitor;